import { Injectable } from '@angular/core';
import { OfficerDetailFacade } from '@intrensic/shared/officer-details';
import { BaseApiService, aaDataResponse } from '@shared/data-access';
import {
  NewCategoryRequest,
  UpdateCategoryRequest,
  AgencyDetailsCustomerResponse,
  AgencyDetailsResponse,
  AgencyDetailsUpdateObject,
  AgencyDetailsUpdateObjectResponse,
  AgencySettingsResponse,
  AgencySettingsUpdateRequest,
  BaseAgencyDetailsService,
  CategoryResponse,
  Category,
  SelfResponse,
} from '@shared/feature/agency-details';
import { UserRole } from '@shared/utils/base-auth';
import { CadCategory } from '@shared/utils/category';
import { Observable, first, map, switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AgencyDetailsService extends BaseAgencyDetailsService {
  constructor(api: BaseApiService, private officerFacade: OfficerDetailFacade) {
    super(api);
  }

  fetchAgencyDetails(): Observable<AgencyDetailsResponse> {
    return this.officerFacade.getLoggedInOfficer().pipe(
      first(),
      switchMap((officer) => {
        if (officer.role === UserRole.SuperAdmin || officer.role === UserRole.HRAdmin) {
          return this.api.get<AgencyDetailsResponse>('api/customers').pipe(
            map((responseValue) => {
              responseValue.uidIdentifier = responseValue.UID;

              return responseValue;
            })
          );
        }

        return this.api.get<AgencyDetailsCustomerResponse>('api/profile').pipe(
          map((response: AgencyDetailsCustomerResponse) => {
            const agency = new AgencyDetailsResponse(
              response.Customer.UID,
              response.Customer.Name,
              response.Customer.PictureFullPath,
              response.Customer.CanDownloadClient,
              response.Customer.CanSelectiveUpload,
              response.Customer.CanDeleteFiles,
              response.Customer.IsLoginPinRequired,
              response.Customer.AwsInfo,
              response.Customer.CaseDownloadAvailableDays,
              response.Customer.ShortName,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              response.Customer.CanUseCad,
              response.Customer.CanUseSelfRedaction
            );
            agency.uidIdentifier = response.Customer.UID;

            return agency;
          })
        );
      })
    );
  }

  fetchCategories(uid: string): Observable<aaDataResponse<CategoryResponse>> {
    return this.api.get<aaDataResponse<CategoryResponse>>(`api/customers/${uid}/categories`);
  }

  fetchSettings(): Observable<AgencySettingsResponse> {
    return this.officerFacade.getLoggedInOfficer().pipe(
      first(),
      switchMap((x) => {
        if (x.role === UserRole.SuperAdmin) {
          return this.api.get<AgencySettingsResponse>('api/customers/settings');
        }

        return this.api.get<SelfResponse>('api/profile').pipe(
          map((response: SelfResponse) => {
            return this.mapToAgencySettings(response);
          })
        );
      })
    );
  }

  editAgencyDetails(details: AgencyDetailsUpdateObject): Observable<AgencyDetailsUpdateObjectResponse> {
    return this.api.patch('api/customers', details);
  }

  editPointOfContact(details: AgencyDetailsUpdateObject): Observable<AgencyDetailsUpdateObjectResponse> {
    return this.api.patch('api/customers', details);
  }

  editAgencySettings(settings: AgencySettingsUpdateRequest): Observable<AgencySettingsResponse> {
    return this.api.patch('api/customers/settings', settings);
  }

  addNewAgencyCategory(category: NewCategoryRequest): Observable<Category> {
    return this.api.post<CategoryResponse>(`api/categories`, category).pipe(
      map((response: CategoryResponse) => {
        return this.transformAgencyCategoryResponse(response);
      })
    );
  }

  editAgencyCategory(uid: string, category: UpdateCategoryRequest): Observable<Category> {
    return this.api.patch<CategoryResponse>(`api/categories/${uid}`, category).pipe(
      map((response: CategoryResponse) => {
        return this.transformAgencyCategoryResponse(response);
      })
    );
  }

  deleteAgencyCategory(uid: string): Observable<string> {
    return this.api.delete(`api/categories/${uid}`);
  }

  private transformAgencyCategoryResponse(response: CategoryResponse): Category {
    return new Category(
      response.Name,
      response.RetainDays,
      response.UseRetainDays,
      response.IsArchived,
      response.IsDefault,
      response.CadCategories.map((x) => new CadCategory(x.Name, x.Uid)),
      response.AppliedOnFiles,
      response.UID,
      response.Description
    );
  }

  private mapToAgencySettings(response: SelfResponse): AgencySettingsResponse {
    return new AgencySettingsResponse(
      response.Customer.CanDownloadClient,
      response.Customer.CanSelectiveUpload,
      response.Customer.DownloadReasonMinLength,
      response.Customer.IsLoginPinRequired,
      response.Customer.UseDownloadReason,
      response.Customer.UseMilitaryTime
    );
  }
}
